import { generateTemporaryId } from '@pn/core/utils/id';
import { hasKey } from '@pn/core/utils/logic';
import { isEmpty, isNil } from 'lodash-es';
import type { Enterprise, UserEnterprise } from './enterprise';
import type { ISODateTimeString } from './types';

export const enum UserPlans {
  Guest = 'guest',
  Free = 'free',
  Professional = 'professional',
  EnterpriseMember = 'enterprise_member',
  EnterpriseAdmin = 'enterprise_admin',
  EnterpriseOwner = 'enterprise_owner',
}

/**
 * Enums for intel role. Do NOT change these! They are used in the database.
 */
export const enum IntelPlans {
  Starter = 'starter',
  Enterprise = 'market_intel_enterprise',
}

export type BaseUser = {
  id: string;
  email: string;
  enterprise:
    | {
        id: Enterprise['id'];
        name: Enterprise['name'];
        stackCompanyId: Enterprise['stackCompanyId'];
      }
    | undefined;
  userPlan: UserPlans;
  intelPlan: IntelPlans;
};

export type User = {
  id: string;
  email: string;
  name: {
    firstName: string;
    lastName: string;
  };
  company: string;
  title: string;
  userPlan: UserPlans;
  authenticationType: string | undefined;
  enterprise: UserEnterprise | undefined;
  stackCompanyId: string | undefined;
  isInvited: boolean;
  createdAt?: ISODateTimeString;
  intelPlan: IntelPlans;
  hasGptAccess: boolean;
  gptUsage: {
    freeCreditsRemaining: number;
    paidCreditsBalance: number;
    freeCreditsResetAt: ISODateTimeString;
  };
  stripeAccount:
    | {
        createdAt: ISODateTimeString;
        currency: string;
        delinquent: boolean;
        email: string;
        id: string;
      }
    | undefined;
  isAdmin: boolean;
};

export function isEnterpriseUser(user: User | undefined): boolean {
  if (isNil(user)) return false;
  return (
    (user.userPlan === UserPlans.EnterpriseAdmin ||
      user.userPlan === UserPlans.EnterpriseMember) &&
    !isNil(user.enterprise)
  );
}

export type UserForm = {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  title: string;
};

export type InvitedUser = BaseUser & {
  createdAt: ISODateTimeString;
};

type InvitedUserForm = {
  email: string;
  userPlan: UserPlans;
  enterpriseId: Enterprise['id'];
};

export type ProjectUser = Pick<User, 'id' | 'email' | 'name'>;

export function userToProjectUser(user: User): ProjectUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
  };
}

export function getFullName(user: Partial<User> | undefined): string {
  if (
    isNil(user) ||
    !hasKey(user, 'name') ||
    isNil(user.name) ||
    isEmpty(user.name.firstName) ||
    isEmpty(user.name.lastName)
  ) {
    return 'N/A';
  } else {
    return `${user.name.firstName} ${user.name.lastName}`;
  }
}

export function getStripeStatus(
  user: User | undefined
): 'current' | 'delinquent' | undefined {
  if (
    isNil(user) ||
    isEmpty(user.name.firstName) ||
    isEmpty(user.name.lastName) ||
    isNil(user.stripeAccount)
  ) {
    return undefined;
  } else if (!isEmpty(user.stripeAccount?.id)) {
    if (user.stripeAccount.delinquent) return 'delinquent';
    return 'current';
  }
}

export function getPermissionLevel(userPlan: UserPlans | undefined): number {
  switch (userPlan) {
    case undefined:
    case UserPlans.Guest:
      return 0;
    case UserPlans.Free:
      return 1;
    case UserPlans.Professional:
      return 2;
    case UserPlans.EnterpriseMember:
      return 3;
    case UserPlans.EnterpriseAdmin:
    case UserPlans.EnterpriseOwner:
      return 4;
  }
}

export function getIntelPermissionLevel(
  userIntelPlan: IntelPlans | undefined
): number {
  switch (userIntelPlan) {
    case undefined:
    case IntelPlans.Starter:
      return 0;
    case IntelPlans.Enterprise:
      return 1;
  }
}

export function createUser(
  userForm: UserForm
): Pick<User, 'id' | 'email' | 'name' | 'company' | 'title'> {
  return {
    id: generateTemporaryId(),
    email: userForm.email,
    name: {
      firstName: userForm.firstName,
      lastName: userForm.lastName,
    },
    company: userForm.company,
    title: userForm.title,
  };
}

export function createInvitedUser(userForm: InvitedUserForm): InvitedUser {
  return {
    id: generateTemporaryId(),
    email: userForm.email,
    userPlan: userForm.userPlan,
    enterprise: {
      id: userForm.enterpriseId,
      name: '',
      stackCompanyId: undefined,
    },
    createdAt: new Date().toISOString(),
    intelPlan: IntelPlans.Starter,
  };
}

export function updateUserProfile(user: User, userForm: UserForm): User {
  return {
    ...user,
    email: userForm.email,
    name: {
      firstName: userForm.firstName,
      lastName: userForm.lastName,
    },
    company: userForm.company,
    title: userForm.title,
  };
}

export function getFormattedUserPlan(userPlan: UserPlans | undefined): string {
  switch (userPlan) {
    case undefined:
    case UserPlans.Guest:
      return 'Guest';
    case UserPlans.Free:
      return 'Free Plan';
    case UserPlans.Professional:
      return 'Professional Plan';
    case UserPlans.EnterpriseMember:
      return 'Enterprise Member';
    case UserPlans.EnterpriseAdmin:
      return 'Enterprise Admin';
    case UserPlans.EnterpriseOwner:
      return 'Enterprise Owner';
  }
}

export function getFormattedIntelPlan(
  intelPlan: IntelPlans | undefined
): string {
  switch (intelPlan) {
    case undefined:
    case IntelPlans.Starter:
      return 'Starter';
    case IntelPlans.Enterprise:
      return 'Enterprise';
  }
}
